import React from 'react';
import {
  RegistrationScreen,
  type RegistrationConfig,
} from './registration-common.js';

/**
 * Eye colour / first school attended (CCF-60 mandatory items) have NO
 * reliable on-file source to verify against — ai-review HIGH (#311, raised
 * again after the earlier "require enough DISTINCT answers" fix): the real
 * gateway's `customer/info.SecurityQuestions` field (confirmed against the
 * Unibanks reference contract: `type: string, maxLength: 60`, "Security
 * Questions for Broker24 & Business online customers") is a single
 * schema-less free-text field with NO delimiter contract, ordering or
 * per-question tagging — an agent free-types into it elsewhere, covering
 * BOTH these 2 mandatory facts AND the 4 generic "additional" questions
 * below. A minimum-distinct-count gate only checks volume, not identity: the
 * fixture `['Hurling', 'Oak Avenue', 'Jazz']` (a sport, a street, a music
 * genre) still cleared any count threshold and let an agent mark BOTH
 * mandatory rows Pass despite neither an eye colour nor a school name being
 * present on file — a fail-open, security-sensitive gap. #311's fix made
 * both rows FAIL CLOSED unconditionally (never resolve a value, "Not on
 * file", Pass control disabled) as CCF-60's "Insufficient Data on File"
 * unhappy path.
 *
 * Issue #349 (Dylan, revising #311 on this same ticket) walks that back: the
 * agent CAN ask the customer directly and judge Pass/Fail by eye/ear against
 * the same shared free-text list rendered in "additional questions" below —
 * there is just no automatic on-file VALUE to display or diff against for
 * these 2 specifically-named facts (unlike the 4 generic additional
 * questions, whose >= N distinct-answer count still gates them). That is
 * exactly the `MandatoryItem.externalCheck` pattern already used for VISA's
 * TSYS checks (`VisaRegistrationScreen`) and Mortgage's Hogan checks
 * (`MortgageRegistrationScreen`): question + hint + always-markable Pass/Fail,
 * no on-file value ever shown. Reused here rather than inventing a new UI
 * state. The former fail-closed "Not on file" / disabled rendering and its
 * `noReliableOnFileSource` resolver are removed; the hint now points the
 * agent at the additional-questions list instead of explaining why no value
 * is shown.
 */
const SEE_ADDITIONAL_ANSWERS_HINT =
  'See answers below in additional questions.';

/**
 * SCRN 21 — Broker24 Registration (CCF-60). Mirrors the Figma
 * `update-reg-broker-24` design: broker's full name + signatory address + eye
 * colour + first school attended mandatory identity items, then any 1 of 4
 * additional questions, mandatory scripting and "Transfer to automated
 * self-service". No 2FA step (faithful to the Figma). Reached after a
 * deregistration when the online-card type is BROKER24 (CCF-8/CCF-46).
 *
 * The 4 additional questions' on-file answers are `additionalAnswersUnordered`
 * (issue #311, mirroring issue #305's ruling for Business24): they are free
 * text from `customer/info`, with no guaranteed order or 1:1 mapping to the 4
 * questions below (an agent free-types into the source field elsewhere) — so
 * no `answer` resolver is wired per-question; `RegistrationScreen` renders the
 * on-file answers as a single, unordered list instead. `additionalSelectable`
 * (issue #349) additionally puts a tick-box in front of each of the 4
 * questions: the agent ticks the ONE question they are asking (ticking a
 * different box deselects the previous one) and only that question's
 * Pass/Fail control is shown — CCF-60 only ever needs 1 of the 4 answered.
 *
 * The eye-colour / first-school mandatory rows are `externalCheck` (issue
 * #349, revising #311's original unconditional fail-closed rule — see the
 * {@link SEE_ADDITIONAL_ANSWERS_HINT} doc above): the agent judges Pass/Fail
 * by asking the customer and checking against the shared free-text answers
 * list shown in "additional questions" below, the same pattern already used
 * for VISA's TSYS checks and Mortgage's Hogan checks — no on-file value is
 * ever shown for these 2 rows, but the Pass/Fail control is always markable.
 *
 * The transfer note carries CCF-60's Mortgage-Broker (email IMC team) and
 * Deposit-Broker (ring Signature Deposits) temporary-password activation steps.
 */
const CONFIG: RegistrationConfig = {
  screenCode: 'SCRN-21',
  heading: 'Broker 24 registration',
  mandatory: [
    { key: 'name', label: "Broker's full name", value: (_q, name) => name },
    {
      key: 'signatoryAddress',
      label: 'Signatory address',
      value: (q) => q.SignatoryAddress,
    },
    {
      key: 'eye',
      label: 'Can you confirm your eye colour?',
      hint: SEE_ADDITIONAL_ANSWERS_HINT,
      externalCheck: true,
      value: () => undefined,
    },
    {
      key: 'school',
      label: 'Can you confirm the first school you attended?',
      hint: SEE_ADDITIONAL_ANSWERS_HINT,
      externalCheck: true,
      value: () => undefined,
    },
  ],
  additional: [
    {
      key: 'street',
      label: 'Can you confirm the name of the street you grew up on?',
    },
    { key: 'music', label: 'Can you confirm your favourite type of music?' },
    {
      key: 'holiday',
      label: 'Can you confirm your favourite holiday destination?',
    },
    { key: 'sport', label: 'Can you confirm your favourite sport?' },
  ],
  requiredAdditional: 1,
  twoFactor: false,
  additionalAnswersUnordered: true,
  additionalSelectable: true,
  additionalInstructions:
    'Tick the box beside the ONE question you are asking the customer, then ' +
    'mark Pass or Fail against the answers on file shown above.',
  transferNote:
    'For Mortgage Brokers: advise the broker to email the IMC team to have the ' +
    'temporary internet password activated. For Deposit Brokers: advise the ' +
    'broker to ring the Signature Deposits team to have it activated.',
  // #653 — once all questions pass, offer the Reset Internet Password option
  // with a scoped Back round-trip (@dylan219). Broker24 has no 2FA, so the
  // completion elevates the session server-side to authorise the reset.
  ipwReset: true,
  // #719 (CCRS-3498): Broker24-specific failure copy replaces the generic
  // "visit a branch" message and referral note.
  failureText:
    'Sorry, the information you gave us does not match our records, so security has failed.',
  failureNote:
    'Advise the broker to contact their Broker Representative / IMC.',
  // #772 (CCF-318 Unhappy Path #2): when the broker has no security questions on
  // file (the free-text answers list is empty) there is nothing to verify the
  // additional questions or the eye-colour / first-school external checks
  // against, so show the "not enough information on file" note and block
  // completion. Broker24 only — Business24 stays always-markable (#648).
  insufficientDataWhenNoAnswers: true,
};

export function Broker24RegistrationScreen(): React.JSX.Element {
  return <RegistrationScreen config={CONFIG} />;
}
